"use client";

import { defaultSupportAgentId } from "@/constants";
import { usePathname } from "@/i18n/navigation";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useSupportAgentConfig } from "./SupportAgent/ConfigProvider";
import { AgentVisibilityProvider, useAgentVisibility } from "./SupportAgent/VisibilityProvider";

// Dynamic imports for code splitting
const FloatingAgent = dynamic(() => import("@ui/components/routes/conversational-ai/FloatingAgent/Initial"), {
  ssr: false,
});

const ConversationalAIPageAgentProvider = dynamic(
  () =>
    import("@ui/components/routes/conversational-ai/FloatingAgent/Provider").then(
      mod => mod.ConversationalAIPageAgentProvider,
    ),
  {
    ssr: false,
  },
);

function FloatingAgentWrapper({ hiddenPaths }: { hiddenPaths: string[] }) {
  const pathname = usePathname();
  const [isVisible, setIsVisible] = useState(false);
  const { isFooterVisible, isOmniboxVisible } = useAgentVisibility();
  const { config, hasActiveCall } = useSupportAgentConfig();
  const t = useTranslations("ClientKeys.ConvAiSupportAgent");

  // Handle pathname-based visibility
  useEffect(() => {
    const shouldHide = hiddenPaths.some(hiddenPath => pathname.includes(hiddenPath));
    setIsVisible(!shouldHide);
  }, [pathname, hiddenPaths]);

  // Combine all visibility conditions
  // - config.hidden: Page explicitly wants to hide layout agent (e.g., has its own custom agent)
  // - hasActiveCall: Keep visible during active call when navigating to hidden paths
  // Note: config.hidden takes precedence because if a page has a custom agent, any active call
  // belongs to that agent, not this layout-level one
  const shouldShow = !config.hidden && (hasActiveCall || (isVisible && !isFooterVisible && !isOmniboxVisible));

  return (
    <div
      className={`transition-opacity duration-300 ease-in-out ${!shouldShow ? "pointer-events-none opacity-0" : "opacity-100"}`}
      inert={!shouldShow || undefined}
    >
      <ConversationalAIPageAgentProvider>
        <FloatingAgent
          agentId={defaultSupportAgentId}
          position='right'
          variant='floating_right'
          responsiveButtonText={{ mobile: t("chat"), desktop: t("voice_chat") }}
        />
      </ConversationalAIPageAgentProvider>
    </div>
  );
}

export default function SupportAgentDynamic({ hiddenPaths }: { hiddenPaths: string[] }) {
  return (
    <AgentVisibilityProvider>
      <FloatingAgentWrapper hiddenPaths={hiddenPaths} />
    </AgentVisibilityProvider>
  );
}
